import styled from 'styled-components'

import InfoTooltip from '@/app/components/InfoToolTip'
import SectionTitle from '@/app/components/SectionTitle'
import SubTitle from '@/app/components/SubTitle'
import TwitterLink from '@/app/components/TwitterLink'
import useWindowDimensions from '@/app/hooks/useWindowDimensions'
import { colors, spaces } from '@/config/global-parameters'
import { percentFormatter } from '@/helpers/formatters'
import PlayIcon from '@/images/PlayIcon'
import { showInDesktop, showInMobile } from '@/styles/mediaQueries'

import { IGanadorWithData } from '../../consejeros/types/ganadores'
import ProgressBarBase, { Separator } from '../ParticipationSection/ParticipationTable/ProgressBar'
import { partidosDataFromGanadores, whiskerDataFormGanadoresWidData } from './adapters'
import Pacto from './Pacto'
import WhiskerLabel, { Text } from './WhiskerLabel'

export interface IComparadorRechazo {
  titleComparadores?: string
  ganadores: IGanadorWithData[]
  ratioRechazoPlebiscito: number
}

const ComparadorRechazo = ({
  ganadores,
  ratioRechazoPlebiscito,
  titleComparadores,
}: IComparadorRechazo) => {
  const { barras, ratioApruebo, ratioPartidosDerecha } = whiskerDataFormGanadoresWidData({
    ganadores,
    ratioRechazoPlebiscito,
  })

  const { isMobile } = useWindowDimensions()

  const title = titleComparadores
    ? `Comparador Plebiscito 2022: ${titleComparadores}`
    : 'Comparador Plebiscito de Salida 2022'

  const listasData = partidosDataFromGanadores({ ganadores })
  return (
    <Wrapper>
      <SectionTitle>
        <FlexContainer useColumn={isMobile}>
          {title}
          <InfoTooltip
            color={colors.colorBlack}
            id="comparador-rechazo-plebiscito-tooltip"
            message={
              <PopoverContent>
                Este análisis se hace solo con la opción Rechazo ya que fue la ganadora y porque
                otros partidos políticos estaban divididos frente a ambas opciones.
              </PopoverContent>
            }
            place="bottom"
            width="300px"
          />
        </FlexContainer>
      </SectionTitle>
      <SubTitle>
        La votación obtenida en esta elección por los partidos políticos que apoyaron
        institucionalmente la opción Rechazo, ¿logra igualar la votación del plebiscito de salida?
      </SubTitle>
      <TwitterLink />
      <Graph>
        <WhiskerBar>
          <MobilePercentageLabel>
            <AprueboLabel>
              <LabelText>Apruebo</LabelText>
              <div>{percentFormatter.format(Number(ratioApruebo) || 0)}</div>
            </AprueboLabel>
            <div>
              <LabelText>Rechazo</LabelText>
              <RechazoPercentage>
                {percentFormatter.format(Number(ratioRechazoPlebiscito) || 0)}
              </RechazoPercentage>
            </div>
          </MobilePercentageLabel>
          <PlebiscitoWhiskers ratioRechazo={ratioRechazoPlebiscito}>
            <WhiskerLabel color={colors.colorGrey15}>
              Apruebo {percentFormatter.format(Number(ratioApruebo) || 0)}
            </WhiskerLabel>
            <WhiskerLabel>
              Rechazo {percentFormatter.format(Number(ratioRechazoPlebiscito) || 0)}
            </WhiskerLabel>
          </PlebiscitoWhiskers>
          <BarWrapper>
            <TriangleDown color={colors.colorGrey27} />
            <ProgressBar
              height="50px"
              options={barras}
              separatorBlend
            />
            <Mayority>MAYORÍA</Mayority>
          </BarWrapper>
          {Boolean(ratioPartidosDerecha) && (
            <WhiskerDerecha ratio={ratioPartidosDerecha}>PARTIDOS DE DERECHA</WhiskerDerecha>
          )}
        </WhiskerBar>
        <Listas columns={listasData.length}>
          {listasData.map((listaData) => (
            <Pacto
              key={listaData.nombre}
              {...listaData}
            />
          ))}
        </Listas>
        <ListasMobile>
          {listasData.map((listaData) => (
            <PartyWrapper
              color={listaData.color}
              key={listaData.nombre}
            >
              <PartySquare color={listaData.color} />
              <div>{listaData.nombre}</div>
            </PartyWrapper>
          ))}
        </ListasMobile>
      </Graph>
    </Wrapper>
  )
}

const FlexContainer = styled.div<{ useColumn?: boolean }>`
  display: flex;
  flex-flow: ${({ useColumn }) => (useColumn ? 'column' : 'row')};
  justify-content: center;
  align-items: center;
  text-align: center;
`

const MobilePercentageLabel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  font-size: 20px;
  font-weight: 700;
  ${showInMobile}
`

const Mayority = styled.div`
  font-size: 12px;
  color: ${colors.colorGrey27};
  align-self: center;
  justify-self: center;
  padding-top: ${spaces.xxs};
`

const BarWrapper = styled.div`
  display: flex;
  flex-flow: column;
  width: 100%;
`

const TriangleDown = styled(PlayIcon)<{ color?: string }>`
  width: 8px;
  height: 8px;
  margin-right: -4px;
  transform: rotate(90deg);
  path {
    fill: ${({ color }) => color};
  }
  align-self: center;
  justify-self: center;
`

const AprueboLabel = styled.div`
  color: ${colors.colorGrey15};
`

const LabelText = styled.div`
  font-size: 18px;
  font-weight: 400;
`

const RechazoPercentage = styled.div`
  text-align: right;
`

const Listas = styled.div<{ columns: number }>`
  display: grid;
  gap: ${spaces.xs} ${spaces.m};
  grid: repeat(4, min-content) / repeat(${({ columns }) => columns}, 1fr);
  grid-auto-flow: column;
  width: min-content;
  ${showInDesktop}
`

const ListasMobile = styled.div`
  display: flex;
  gap: ${spaces.s};
  font-size: 13px;
  justify-content: space-around;
  width: 100%;
  font-weight: 700;
  white-space: break-spaces;
  padding: 0 ${spaces.s};
  ${showInMobile}
`

const PartyWrapper = styled.div<{ color: string }>`
  color: ${({ color }) => color};
  display: flex;
  gap: ${spaces.s};
  align-items: center;
`

const PartySquare = styled.div<{ color: string }>`
  background-color: ${({ color }) => color};
  width: 8px;
  height: 8px;
  min-width: 8px;
  min-height: 8px;
`

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  width: 100%;
  margin: ${spaces.m} 0;
`

const Graph = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: ${spaces.l};
  justify-content: center;
  align-items: center;
  padding: 0 ${spaces.xs};
`

const WhiskerBar = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: ${spaces.xs};
  justify-content: center;
  align-items: center;
`

const ProgressBar = styled(ProgressBarBase)`
  ${Separator} {
    mix-blend-mode: normal;
  }
`

const PlebiscitoWhiskers = styled.div<{ ratioRechazo: number }>`
  display: grid;
  grid: 1fr / auto ${({ ratioRechazo }) => ratioRechazo * 100}%;
  width: 100%;
`

const WhiskerDerecha = styled(WhiskerLabel)<{ ratio: number }>`
  align-self: flex-end;
  width: ${({ ratio }) => ratio * 100}%;
  ${Text} {
    font-weight: 400;
    font-size: 12px;
  }
  ${showInDesktop}
`

const PopoverContent = styled(SubTitle)`
  line-height: 10px;
  font-size: 10px;
`

export default ComparadorRechazo
